/**
 * Quantity-mode (MDOT-24700) column behavior. Column *definitions* stay centralized in
 * `$root/ui-lib/table/column` (`columnsDefinition.instrumentQuantityComposition`); this
 * decorator only attaches the quantity commit handlers on top of them. WEIGHT is declared
 * `editable: false` in the quantity column set (read mode and column-state builds stay
 * inert) — edit mode enables it HERE, and the shared table skips its own WEIGHT handler in
 * quantity mode (the re-read value it receives is stale, see `commit`).
 *
 * The unpriced (MDOT-25191) and held-by-value (MDOT-25244) row states are layered here for the
 * same reason: they belong to the quantity editor, and the shared column definitions are read by
 * every other composition grid.
 */
import type { ColDef, GridApi, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import i18n from "i18next";
import { canonicalize, localeSeparators, normalizePastedAmount } from "$root/components/monetary-input/format";
import { formatDate } from "$root/localization/formatters";
import type { Instrument } from "../const";
import type { QuantityEditorBuilderProps } from "./builder";
import { isUnpriced } from "./market-date/pricing";
import { HELD_BLOCKED_RESTRICTIONS, isHeldBlocked } from "./held-issues/heldIssue";
import { withHeldIssueCell } from "./held-issues/HeldIssueCell";
import { isLocked } from "./new-instruments/checks";
import { withCheckMarker } from "./new-instruments/withCheckMarker";
import { newInstrumentIssueColumn } from "./new-instruments/withIssueCell";
import { withNotAvailableCell } from "./not-available/withNotAvailableCell";
import { withSizingMarker } from "./position-sizing/withSizingMarker";
import { withSizingMessagePulse } from "./sizing-message/withSizingMessagePulse";

/**
 * What a row state restricts, column by column. `lock`: the cell refuses edits, because the state
 * leaves nothing to convert a typed value against. `blank`: the cell reads empty rather than its
 * column's own formatting of a missing value, which for a weight is a zero. `notAvailable`: the cell
 * reads "N/A" — a blank in a numeric cell reads as zero — with an info icon whose tooltip, this
 * function's text, says why the row has no such value; it takes precedence over `blank`.
 */
type ColumnRestrictions = Record<string, { lock?: true; blank?: true; notAvailable?: () => string }>;

/**
 * What the unpriced row state (MDOT-25191) restricts on each quantity column: there is no price at
 * the market date, so nothing the price decides can be shown or typed. The missing price is the
 * row's one N/A, and its tooltip carries the whole explanation; market value and weight are what
 * that price would decide, so they read empty. Quantity is locked but still shown — how many shares
 * are held is still known.
 */
function unpricedRestrictions(builder: Pick<QuantityEditorBuilderProps, "getMarketDate">): ColumnRestrictions {
	return {
		QUANTITY: { lock: true },
		MARKET_PRICE: {
			notAvailable: () => i18n.t("TABLE.QUANTITY.NA.UNPRICED", { date: formatDate(builder.getMarketDate().value) }),
		},
		MARKET_VALUE: { lock: true, blank: true },
		WEIGHT: { lock: true, blank: true },
	};
}

/**
 * What a row held by value (MDOT-25244) restricts on each quantity column: it is sized in money
 * rather than in shares, so it has no share count and no unit price — both read "N/A" with a
 * tooltip saying why, and a typed quantity is refused at the cell rather than silently dropped by
 * the engine. Market value and weight are left alone: they are exactly what such a row is sized by.
 */
const HELD_BY_VALUE_RESTRICTIONS: ColumnRestrictions = {
	QUANTITY: { lock: true, notAvailable: () => i18n.t("TABLE.QUANTITY.NA.HELD_BY_VALUE") },
	MARKET_PRICE: { notAvailable: () => i18n.t("TABLE.QUANTITY.NA.HELD_BY_VALUE_PRICE") },
};

/**
 * What an added instrument locked by its check restricts: it holds nothing until the check clears
 * it, so nothing about the position can be typed. Its missing price is the check marker's to show.
 */
const CHECK_LOCKED_RESTRICTIONS: ColumnRestrictions = {
	QUANTITY: { lock: true },
	MARKET_VALUE: { lock: true },
	WEIGHT: { lock: true },
};

/** Layers `restrictions` onto a column, for the rows `applies` names. Every other row is untouched. */
function withRowState(
	column: ColDef<Instrument>,
	applies: (row: Instrument) => boolean,
	restrictions: ColumnRestrictions,
): ColDef<Instrument> {
	const { lock, blank, notAvailable } = restrictions[column.colId ?? ""] ?? {};
	const { editable, valueFormatter } = column;
	const restricted: ColDef<Instrument> = {
		...column,
		...(lock && {
			editable: (params) =>
				params.data != null && applies(params.data) ? false : typeof editable === "function" && editable(params),
		}),
		...(blank &&
			!notAvailable && {
				valueFormatter: (params: ValueFormatterParams<Instrument>) =>
					params.data != null && applies(params.data)
						? ""
						: typeof valueFormatter === "function"
							? valueFormatter(params)
							: "",
			}),
	};
	return notAvailable
		? withNotAvailableCell(restricted, (row) => (applies(row) ? notAvailable() : undefined))
		: restricted;
}

/**
 * The row states the quantity grid layers on top of the shared column definitions. The first three
 * never overlap on one row — the pricing engine's answer is one or the other, and a row the
 * new-instrument check has locked has not been priced. A held row blocked by its issue
 * (`./held-issues`) can be any of them as well, and only locks, so the order is free.
 */
function quantityRowStates(
	builder: Pick<QuantityEditorBuilderProps, "getMarketDate">,
): (column: ColDef<Instrument>) => ColDef<Instrument> {
	const unpriced = unpricedRestrictions(builder);
	return (column) =>
		withRowState(
			withRowState(
				withRowState(
					withRowState(column, isUnpriced, unpriced),
					(row) => Boolean(row.heldByValue),
					HELD_BY_VALUE_RESTRICTIONS,
				),
				isLocked,
				CHECK_LOCKED_RESTRICTIONS,
			),
			isHeldBlocked,
			HELD_BLOCKED_RESTRICTIONS,
		);
}

/** A minus before the first digit, ahead of the currency ("-EUR 1") or behind it ("EUR -1"). */
const SIGN_BEFORE_DIGITS = /^(\D*)[-−]/;

/**
 * Reads a pasted or typed amount the way the monetary input does: a currency code or symbol around
 * it and foreign separators are tolerated ("EUR 1,234.00", "€ 1.234,00"). A minus before the first digit
 * is kept for the builder's sanitising to clamp; text with no digit is NaN; an emptied cell is null.
 */
export function parseAmount(params: Pick<ValueParserParams, "newValue">): number | null {
	const { newValue } = params;
	if (typeof newValue === "number" || newValue == null) {
		return newValue ?? null;
	}
	const text = String(newValue).trim();
	if (text === "") {
		return null;
	}
	const separators = localeSeparators(i18n.language);
	const negative = SIGN_BEFORE_DIGITS.test(text);
	const unsigned = text.replace(SIGN_BEFORE_DIGITS, "$1");
	const canonical = canonicalize(normalizePastedAmount(unsigned, separators), separators);
	if (!/\d/.test(canonical)) {
		return Number.NaN;
	}
	return negative ? -Number(canonical) : Number(canonical);
}

/**
 * Returns a copy of `columns` where the QUANTITY and MARKET_VALUE columns commit their
 * edits through the quantity builder. The commit happens inside the `valueSetter` — NOT in
 * `onCellValueChanged` — because AG Grid re-reads the cell through the valueGetter after the
 * setter runs and dispatches `onCellValueChanged` with that re-read value, discarding what
 * the user typed; the typed value only exists in the setter's `params.newValue`. This also
 * covers clipboard paste and the fill handle, which route through the same setter.
 * `onCellValueChanged` is kept for the row-refresh side effect only (it needs just the id).
 * Safe to build from a captured `builder` reference: the wrapper's methods always act on the
 * latest state (see `useQuantityEditorBuilder`).
 */
export function withQuantityCommitHandlers(
	columns: ColDef<Instrument>[],
	builder: QuantityEditorBuilderProps,
): ColDef<Instrument>[] {
	const commit = (
		apply: (rowId: string, rawValue: number) => void,
	): Pick<ColDef<Instrument>, "valueSetter" | "onCellValueChanged"> => {
		return {
			valueSetter(params) {
				apply(params.data.id, params.newValue ?? 0);
				return true;
			},
			onCellValueChanged(params) {
				refreshQuantityModeRows(params.api, builder, params.data.id);
			},
		};
	};
	const withRowStates = quantityRowStates(builder);

	return columns.flatMap((column) => {
		// An added instrument's check (see `./new-instruments`), or a held instrument's blocking
		// issue (see `./held-issues`), shows next to its name.
		if (column.colId === "NAME") {
			return [column, withHeldIssueCell(newInstrumentIssueColumn(builder))];
		}
		if (column.colId === "QUANTITY") {
			return withSizingMarker(
				withSizingMessagePulse(
					withRowStates({
						...column,
						valueParser: parseAmount,
						...commit((rowId, rawValue) => builder.setQuantity(rowId, rawValue)),
					}),
				),
			);
		}
		if (column.colId === "MARKET_VALUE") {
			return withSizingMarker(
				withRowStates({
					...column,
					valueParser: parseAmount,
					...commit((rowId, rawValue) => builder.setMarketValue(rowId, rawValue)),
				}),
			);
		}
		if (column.colId === "MARKET_PRICE") {
			return withCheckMarker(withSizingMarker(withRowStates(column)));
		}
		if (column.colId === "WEIGHT") {
			return withSizingMarker(
				withRowStates({
					...column,
					// A typed weight is sized on Cold War like a typed market value (see the
					// builder's setWeight override). The cash row's weight is the derived AUM remainder — never
					// editable; `data == null` covers group and pinned-total rows.
					editable: (params) => params.data != null && !params.data.isCash && !params.data.deleted,
					...commit((rowId, rawValue) => builder.setWeight(rowId, rawValue)),
				}),
			);
		}
		return column;
	});
}

/**
 * After a quantity/market-value commit, push the edited row and the rebalanced cash row
 * back into the grid and refresh the pinned-bottom rows — imperatively from the event
 * handler (house style), not via a syncing effect. The transaction also re-runs the
 * aggregations that feed the pinned-bottom grand total row. The pinned-bottom container
 * holds the pinned cash copy (see `./pinnedCashRow`) followed by the grand total row, so
 * refresh all of it rather than assuming an index.
 */
function refreshQuantityModeRows(api: GridApi<Instrument>, builder: QuantityEditorBuilderProps, editedId: string) {
	const composition = builder.latestComposition();
	const editedRow = composition.get(editedId);
	const cashRow = composition.find((instrument) => Boolean(instrument.isCash));
	api.applyTransactionAsync({ update: [editedRow, cashRow].filter((row) => row != null) }, () => {
		const pinnedRows = Array.from({ length: api.getPinnedBottomRowCount() }, (_, index) =>
			api.getPinnedBottomRow(index),
		).filter((row) => row != null);
		if (pinnedRows.length > 0) {
			api.refreshCells({ rowNodes: pinnedRows, force: true });
		}
	});
}
